import React, { useMemo, useState } from 'react';
import Editor from '@monaco-editor/react';

function isScalarValue(value) {
  return value === null || ['string', 'number', 'boolean'].includes(typeof value);
}

function getTheme() {
  if (typeof document === 'undefined') {
    return 'vs';
  }

  return document.documentElement.classList.contains('dark') ? 'vs-dark' : 'vs';
}

export default function EmailTemplateFallbackEditor({
  emailtemplateId,
  title,
  topolData,
  mergeFields,
  legacyTopolUrl,
}) {
  const initialTopolData = useMemo(() => {
    if (topolData && typeof topolData === 'object' && !Array.isArray(topolData)) {
      return topolData;
    }

    return {};
  }, [topolData]);

  const [html, setHtml] = useState(String(initialTopolData.html || ''));
  const [jsonText, setJsonText] = useState(
    JSON.stringify(initialTopolData.json ?? {}, null, 2)
  );
  const [testEmail, setTestEmail] = useState('');
  const [status, setStatus] = useState(null);

  const [extraFields, setExtraFields] = useState(() => {
    return Object.entries(initialTopolData)
      .filter(([key]) => key !== 'html' && key !== 'json')
      .map(([key, value]) => ({
        key,
        mode: isScalarValue(value) ? 'text' : 'json',
        value: isScalarValue(value) ? String(value ?? '') : JSON.stringify(value, null, 2),
      }));
  });

  const flattenedMergeFields = useMemo(() => {
    return Array.isArray(mergeFields) ? mergeFields : [];
  }, [mergeFields]);

  const setExtraFieldValue = (index, nextValue) => {
    setExtraFields((current) => current.map((field, fieldIndex) => (
      fieldIndex === index ? { ...field, value: nextValue } : field
    )));
  };

  const buildPayload = () => {
    let parsedJson;

    try {
      parsedJson = jsonText.trim() ? JSON.parse(jsonText) : {};
    } catch (error) {
      throw new Error(`Template JSON is invalid: ${error.message}`);
    }

    const payload = {
      html,
      json: parsedJson,
    };

    for (const field of extraFields) {
      if (!field.key) {
        continue;
      }

      if (field.mode === 'json') {
        try {
          payload[field.key] = field.value.trim() ? JSON.parse(field.value) : null;
        } catch (error) {
          throw new Error(`${field.key} is invalid JSON: ${error.message}`);
        }
      } else {
        payload[field.key] = field.value;
      }
    }

    return payload;
  };

  const emitLivewireEvent = (eventName, payload) => {
    if (!window.Livewire || typeof window.Livewire.emit !== 'function') {
      throw new Error('Livewire is not available on this page.');
    }

    window.Livewire.emit(eventName, payload);
  };

  const handleSave = (eventName) => {
    try {
      const payload = buildPayload();
      emitLivewireEvent(eventName, payload);
      setStatus({ type: 'success', text: 'Template payload sent for saving.' });
    } catch (error) {
      setStatus({ type: 'danger', text: error.message });
    }
  };

  const handleTestSend = () => {
    try {
      if (!testEmail.trim()) {
        throw new Error('Enter an email address for test send.');
      }

      const payload = buildPayload();
      emitLivewireEvent('test_topol_email', {
        ...payload,
        email: testEmail.trim(),
      });
      setStatus({ type: 'success', text: 'Test email request sent.' });
    } catch (error) {
      setStatus({ type: 'danger', text: error.message });
    }
  };

  return (
    <div className="container-fluid" style={{ padding: '20px' }}>
      <div className="panel panel-default">
        <div className="panel-heading" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
          <div>
            <h3 className="panel-title" style={{ margin: 0 }}>Email Template HTML Fallback</h3>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              Editing template #{emailtemplateId}: {title || 'Untitled template'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {legacyTopolUrl ? (
              <a href={legacyTopolUrl} className="btn btn-default btn-sm">Legacy Topol</a>
            ) : null}
            <button type="button" className="btn btn-primary btn-sm" onClick={() => handleSave('save_topol_email')}>Save</button>
            <button type="button" className="btn btn-success btn-sm" onClick={() => handleSave('save_close_topol_email')}>Save &amp; Close</button>
          </div>
        </div>

        <div className="panel-body">
          <p>
            This fallback keeps the existing <code>topol</code> payload shape and only replaces the editor UI.
            The email-ready HTML stays in <code>topol.html</code>; the design payload stays in <code>topol.json</code>.
          </p>

          {status ? (
            <div className={`alert alert-${status.type}`} role="alert">{status.text}</div>
          ) : null}

          <div className="row">
            <div className="col-md-9">
              <div className="form-group">
                <label className="control-label">HTML</label>
                <div style={{ height: '60vh', border: '1px solid #dcdcdc' }}>
                  <Editor
                    height="100%"
                    defaultLanguage="html"
                    value={html}
                    onChange={(value) => setHtml(String(value ?? ''))}
                    theme={getTheme()}
                    options={{
                      minimap: { enabled: false },
                      fontSize: 13,
                      wordWrap: 'on',
                      automaticLayout: true,
                    }}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="control-label">Topol JSON</label>
                <div style={{ height: '28vh', border: '1px solid #dcdcdc' }}>
                  <Editor
                    height="100%"
                    defaultLanguage="json"
                    value={jsonText}
                    onChange={(value) => setJsonText(String(value ?? ''))}
                    theme={getTheme()}
                    options={{
                      minimap: { enabled: false },
                      fontSize: 13,
                      wordWrap: 'on',
                      automaticLayout: true,
                    }}
                  />
                </div>
              </div>

              {extraFields.map((field, index) => (
                <div className="form-group" key={field.key || `extra-${index}`}>
                  <label className="control-label">{field.key}</label>
                  {field.mode === 'json' ? (
                    <div style={{ height: '20vh', border: '1px solid #dcdcdc' }}>
                      <Editor
                        height="100%"
                        defaultLanguage="json"
                        value={field.value}
                        onChange={(value) => setExtraFieldValue(index, String(value ?? ''))}
                        theme={getTheme()}
                        options={{
                          minimap: { enabled: false },
                          fontSize: 13,
                          wordWrap: 'on',
                          automaticLayout: true,
                        }}
                      />
                    </div>
                  ) : (
                    <input
                      type="text"
                      className="form-control"
                      value={field.value}
                      onChange={(event) => setExtraFieldValue(index, event.target.value)}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="col-md-3">
              <div className="panel panel-info">
                <div className="panel-heading">
                  <strong>Test Send</strong>
                </div>
                <div className="panel-body">
                  <div className="form-group">
                    <label className="control-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={testEmail}
                      onChange={(event) => setTestEmail(event.target.value)}
                      placeholder="name@example.com"
                    />
                  </div>
                  <button type="button" className="btn btn-default btn-sm" onClick={handleTestSend}>Send Test</button>
                </div>
              </div>

              <div className="panel panel-default">
                <div className="panel-heading">
                  <strong>Merge Fields</strong>
                </div>
                <div className="panel-body" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                  {flattenedMergeFields.length ? (
                    <ul className="list-unstyled" style={{ marginBottom: 0 }}>
                      {flattenedMergeFields.map((field) => (
                        <li key={field} style={{ fontFamily: 'monospace', fontSize: '12px', marginBottom: '4px' }}>
                          {`{${field}}`}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted" style={{ marginBottom: 0 }}>No merge fields loaded.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
